/**
 * Phase 5: Real-Time Event Capacity Hook
 *
 * Polls the capacity API endpoint every 30 seconds to keep capacity data fresh.
 * Automatically handles loading states, errors, and cleanup.
 */

import { useState, useEffect, useRef } from 'react'

interface CapacityData {
  event_id: string
  event_title: string
  max_lobster_tails: number | null
  max_tails_per_order: number | null
  current_tail_count: number
  remaining_capacity: number | null
  percent_used: number | null
  total_orders: number
  avg_tails_per_order: number
  is_unlimited: boolean
  is_sold_out: boolean
}

interface UseEventCapacityResult {
  capacity: CapacityData | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch and auto-refresh event capacity data
 *
 * @param eventId - The event ID to fetch capacity for
 * @param pollInterval - Polling interval in milliseconds (default: 30000ms = 30 seconds)
 * @param enabled - Whether polling is enabled (default: true)
 * @returns Capacity data, loading state, error, and manual refetch function
 */
export const useEventCapacity = (
  eventId: string | null,
  pollInterval: number = 30000,
  enabled: boolean = true
): UseEventCapacityResult => {
  const [capacity, setCapacity] = useState<CapacityData | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Use ref to track if component is mounted to avoid state updates after unmount
  const isMountedRef = useRef<boolean>(true)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const fetchCapacity = async () => {
    if (!eventId || !enabled) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/.netlify/functions/get-event-capacity?event_id=${eventId}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch capacity: ${response.status}`)
      }

      const data = await response.json()

      if (isMountedRef.current) {
        setCapacity(data)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch capacity'
      console.error('[useEventCapacity] Error:', errorMessage)

      if (isMountedRef.current) {
        setError(errorMessage)
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    isMountedRef.current = true

    // Initial fetch
    if (eventId && enabled) {
      fetchCapacity()
    }

    // Set up polling
    if (eventId && enabled && pollInterval > 0) {
      intervalRef.current = setInterval(() => {
        fetchCapacity()
      }, pollInterval)
    }

    // Cleanup
    return () => {
      isMountedRef.current = false

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [eventId, enabled, pollInterval])

  return {
    capacity,
    loading,
    error,
    refetch: fetchCapacity
  }
}

export default useEventCapacity
